import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  QuestionMarkCircleIcon, 
  ArrowLeftIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  EnvelopeIcon 
} from '@heroicons/react/24/outline';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "How do I create a game?",
      answer: "Simply click 'Create Game' on the homepage, choose your game type, and follow the setup steps. For AI-powered games, describe what you want and we'll generate the content for you!"
    },
    {
      question: "Are my games private?",
      answer: "By default, games are public and can be shared. You can make them private during creation - private games are only accessible to people with the direct link."
    },
    {
      question: "How do leaderboards work?",
      answer: "Players can join leaderboards by entering their name after playing. Scores are ranked automatically, and you can see your ranking compared to other players."
    },
    {
      question: "Can I edit my game after creating it?",
      answer: "Currently, games cannot be edited after creation. We recommend testing your game content before finalizing. Game editing features are coming soon!"
    },
    {
      question: "How many games can I create?",
      answer: "You can create up to 10 games per hour to prevent spam. There's no limit on total games you can create over time."
    },
    {
      question: "What browsers are supported?",
      answer: "Group Chat Heroes works on all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version for the best experience."
    },
    {
      question: "How do I share my games?",
      answer: "After creating a game, you'll get a shareable link. You can copy this link and share it via social media, email, WhatsApp, or any other platform."
    },
    {
      question: "Are there any costs?",
      answer: "Group Chat Heroes is free to use! Create and play as many games as you want without any charges."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <QuestionMarkCircleIcon className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">Support & FAQ</h1>
          </div>
          <p className="text-gray-600 mt-2">Get help with Group Chat Heroes</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <EnvelopeIcon className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Need More Help?</h2>
          </div>
          <p className="text-gray-700 mb-4">
            Can't find what you're looking for? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@groupchatheroes.com"
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              Email Support
            </a>
            <span className="text-sm text-gray-500 flex items-center">
              We typically respond within 24 hours
            </span>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
          </div>
          
          <div className="divide-y">
            {faqs.map((faq, index) => (
              <div key={index} className="p-6">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-medium text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                
                {openFaq === index && (
                  <div className="mt-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link 
              to="/privacy" 
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              Privacy Policy →
            </Link>
            <Link 
              to="/terms" 
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              Terms of Service →
            </Link>
            <a 
              href="/" 
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              Create a Game →
            </a>
            <a 
              href="/play" 
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              Browse Games →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support; 